import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Customer } from "@/customers/useCustomers";
import dayjs from "dayjs";

const CUSTOMER_CHIP_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
  "bg-orange-100 text-orange-700",
  "bg-fuchsia-100 text-fuchsia-700",
] as const;

const MAX_VISIBLE_CHIPS = 4;

function chipColorClass(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return CUSTOMER_CHIP_COLORS[Math.abs(hash) % CUSTOMER_CHIP_COLORS.length];
}

function formatDateRange(fromDate: Date | null, toDate: Date | null): string {
  const format = (date: Date) => dayjs(date).format("D MMM YYYY");
  if (fromDate && toDate) return `${format(fromDate)} – ${format(toDate)}`;
  if (fromDate) return `From ${format(fromDate)}`;
  if (toDate) return `Until ${format(toDate)}`;
  return "All dates";
}

function customerChipLabel(customer: Customer): string {
  return customer.providerId || customer.label;
}

interface AppliedFiltersSummaryProps {
  fromDate: Date | null;
  toDate: Date | null;
  customers: Customer[];
}

export function AppliedFiltersSummary({ fromDate, toDate, customers }: AppliedFiltersSummaryProps) {
  const visibleCustomers = customers.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCustomers = customers.slice(MAX_VISIBLE_CHIPS);

  return (
    <div className="flex items-center gap-2 text-sm" aria-label="Applied filters">
      <span className="whitespace-nowrap text-muted-foreground" aria-label="Applied date range">
        {formatDateRange(fromDate, toDate)}
      </span>
      {customers.length > 0 && (
        <div className="flex items-center gap-1" aria-label="Applied customers">
          {visibleCustomers.map((customer) => (
            <Badge key={customer.id} variant="outline" className={`border-transparent ${chipColorClass(customer.id)}`}>
              {customerChipLabel(customer)}
            </Badge>
          ))}
          {overflowCustomers.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-default">
                    +{overflowCustomers.length}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{overflowCustomers.map(customerChipLabel).join(", ")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
}
